import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

interface Portfolio {
  tag: string;
  NAV?: number;
  Score?: number;
  x1Day_ReturnsPct?: number;
  x1Day_ReturnsDollar?: number;
  x1Week_ReturnsPct?: number;
  x1Week_ReturnsDollar?: number;
  YTD_Return?: number;
  AnnualizedRet?: number;
  AnnualizedVol?: number;
  MaximumDrawDown?: number;
  BCT?: any[];
  clientAlign?: {
    initialInvest?: number;
  };
  PortPnLDol?: number;
  portId: string;
}

interface PortfolioCardProps {
  portfolio: Portfolio;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ portfolio }) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    // Navigate to portfolio details page with the portfolio ID
    navigate(`/portfolio/${portfolio.portId}`, { 
      state: { portfolio } 
    });
  };

  return (
    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      {/* Header Section */}
      <div style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1rem' }}>
        <h3 style={{ color: '#1e1e1e', marginBottom: '0.5rem' }}>{portfolio.tag}</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e1e1e' }}>
            ${portfolio.NAV?.toLocaleString() || 0}
          </div>
          <div style={{ 
            backgroundColor: '#f0f9ff', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '1rem',
            color: '#0369a1'
          }}>
            Score: {((portfolio.Score || 0) * 100).toFixed(0)}/100
          </div>
        </div>
      </div>

      {/* Performance Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>Today</div>
          <div style={{ 
            color: (portfolio.x1Day_ReturnsPct || 0) > 0 ? '#10B981' : '#EF4444',
            fontWeight: '500'
          }}>
            {portfolio.x1Day_ReturnsPct?.toFixed(2)}% (${portfolio.x1Day_ReturnsDollar?.toFixed(2)})
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>This Week</div>
          <div style={{ 
            color: (portfolio.x1Week_ReturnsPct || 0) > 0 ? '#10B981' : '#EF4444',
            fontWeight: '500'
          }}>
            {portfolio.x1Week_ReturnsPct?.toFixed(2)}% (${portfolio.x1Week_ReturnsDollar?.toFixed(2)})
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>YTD Return</div>
          <div style={{ 
            color: (portfolio.YTD_Return || 0) > 0 ? '#10B981' : '#EF4444',
            fontWeight: '500'
          }}>
            {((portfolio.YTD_Return || 0) * 100).toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>Annualized</div>
          <div style={{ 
            color: (portfolio.AnnualizedRet || 0) > 0 ? '#10B981' : '#EF4444',
            fontWeight: '500'
          }}>
            {portfolio.AnnualizedRet?.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Risk Metrics */}
      <div style={{ 
        backgroundColor: '#f8fafc', 
        padding: '0.75rem', 
        borderRadius: '0.5rem',
        marginBottom: '1rem'
      }}>
        <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#475569' }}>
          Risk Metrics
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Volatility</div>
            <div style={{ color: '#334155' }}>{portfolio.AnnualizedVol?.toFixed(2)}%</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Max Drawdown</div>
            <div style={{ color: '#334155' }}>{portfolio.MaximumDrawDown?.toFixed(2)}%</div>
          </div>
        </div>
      </div>

      {/* Holdings Summary */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#475569' }}>
          Top Holdings
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {Array.isArray(portfolio.BCT) && portfolio.BCT.slice(0, 3).map((holding: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <div style={{ color: '#334155' }}>{holding.Symbol}</div>
              <div style={{ color: '#64748b' }}>{(holding.Allocation * 100).toFixed(2)}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        fontSize: '0.875rem',
        color: '#475569',
        marginBottom: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f8fafc',
        borderRadius: '0.5rem'
      }}>
        <div>
          <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Initial Investment</div>
          <div>${portfolio.clientAlign?.initialInvest?.toLocaleString()}</div>
        </div>
        <div>
          <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Total P/L</div>
          <div style={{ 
            color: (portfolio.PortPnLDol || 0) > 0 ? '#10B981' : '#EF4444'
          }}>
            ${portfolio.PortPnLDol?.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Holdings</div>
          <div>{Array.isArray(portfolio.BCT) ? portfolio.BCT.length : 0}</div>
        </div>
      </div>

      <Button 
        onClick={handleViewDetails}
        style={{ width: '100%' }}
      >
        View Portfolio Details
      </Button>
    </div>
  );
};

export default PortfolioCard;
